import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { History, Search, SlidersHorizontal, ChevronDown, ChevronUp, MessageSquare, HelpCircle, CheckCircle, Hash } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import { PageHeader, UserAvatar } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import "./AI.scss";

const techStacks = techStacksList;
const difficulties = ["", "easy", "medium", "hard"];
const PAGE_SIZE = 10;

const AdminAIAttemptHistory = ({ embedded = false }) => {
  const [attempts, setAttempts] = useState([]);
  const [filters, setFilters] = useState({
    techStack: "",
    difficulty: "",
    minScore: "",
    maxScore: "",
    search: "",
    studentId: ""
  });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-attempts/history", {
        params: {
          page,
          limit: PAGE_SIZE,
          ...filters,
        }
      });
      setAttempts(data.attempts || []);
      setTotalRecords(data.total || data.attempts?.length || 0);
    } catch {
      setAttempts([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAttempts();
  }, [filters, page]);

  const loadStudentOptions = async (inputValue) => {
    try {
      const res = await apiClient.get("/students/list", {
        params: { search: inputValue, limit: 20 }
      });
      return (res.data.students || []).map(s => ({
        label: `${s.name} (${s.email})`,
        value: s._id
      }));
    } catch {
      return [];
    }
  };

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      setFilters(f => ({ ...f, search: searchInput }));
      setPage(1);
    }
  };

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, techStack: "", difficulty: "", minScore: "", maxScore: "" }));
    setPage(1);
  };

  const activeFilterCount = 
    (filters.techStack ? 1 : 0) + 
    (filters.difficulty ? 1 : 0) + 
    (filters.minScore ? 1 : 0) + 
    (filters.maxScore ? 1 : 0);

  const getScoreClass = (score) => {
    if (score >= 70) return "ai-score-badge--high";
    if (score >= 40) return "ai-score-badge--medium";
    return "ai-score-badge--low";
  };

  const getDifficultyClass = (difficulty) => {
    const d = difficulty?.toLowerCase();
    if (d === "easy") return "ai-difficulty-badge--easy";
    if (d === "medium") return "ai-difficulty-badge--medium";
    if (d === "hard") return "ai-difficulty-badge--hard";
    return "";
  };

  // Define table columns
  const columns = [
    {
      key: "index",
      label: "#",
      width: "40px",
      render: (_, index) => (page - 1) * PAGE_SIZE + index + 1
    },
    {
      key: "student",
      label: "Student",
      width: "20%",
      render: (a) => (
        <div className="d-flex align-items-center gap-2">
          <UserAvatar name={a.student?.name} size="sm" />
          <div>
            <div className="fw-medium">{a.student?.name || "-"}</div>
            <div className="text-muted text-sm">{a.student?.email || "-"}</div>
          </div>
        </div>
      )
    },
    {
      key: "question",
      label: "Question",
      width: "25%",
      render: (a) => {
        const q = a.question?.question || "-";
        return (
          <div className="text-sm" title={q}>
            {q.length > 80 ? q.substring(0, 80) + "..." : q}
          </div>
        );
      }
    },
    {
      key: "techStack",
      label: "Tech Stack",
      width: "10%",
      render: (a) => a.question?.techStack || "-"
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (a) => (
        <span className={`ai-difficulty-badge ${getDifficultyClass(a.question?.difficulty)}`}>
          {a.question?.difficulty || "-"}
        </span>
      )
    },
    {
      key: "score",
      label: "Score",
      width: "8%",
      render: (a) => (
        <span className={`ai-score-badge ${getScoreClass(a.score)}`}>
          {a.score ?? "-"}
        </span>
      )
    },
    {
      key: "submittedOn",
      label: "Submitted On",
      width: "12%",
      render: (a) => a.createdAt 
        ? new Date(a.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) 
        : "-"
    },
    {
      key: "expand",
      label: "",
      width: "40px",
      render: (a) => {
        const rowId = a._id || a.id;
        const isExpanded = expandedRows.has(rowId);
        return (
          <button
            className="ai-expand-btn"
            onClick={(e) => { e.stopPropagation(); toggleRow(rowId); }}
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
            title={isExpanded ? "Hide details" : "View details"}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        );
      }
    }
  ];

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "var(--input-bg)",
      borderColor: "var(--border-primary)",
      borderRadius: "8px",
      minHeight: "38px",
      "&:hover": {
        borderColor: "var(--text-tertiary)"
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--card-bg)",
      border: "1px solid var(--border-primary)",
      borderRadius: "8px",
      boxShadow: "0 4px 12px var(--card-shadow)"
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "var(--bg-hover)" : "transparent",
      color: state.isSelected ? "#fff" : "var(--text-primary)",
      "&:hover": {
        backgroundColor: state.isSelected ? "#3b82f6" : "var(--bg-hover)"
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text-primary)"
    }),
    input: (base) => ({
      ...base,
      color: "var(--text-primary)"
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text-tertiary)"
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "var(--border-primary)"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--text-tertiary)",
      "&:hover": {
        color: "var(--text-primary)"
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "var(--text-tertiary)",
      "&:hover": {
        color: "var(--text-primary)"
      }
    })
  };

  return (
    <div className="ai-page">
      {!embedded && (
        <PageHeader
          icon={History}
          title="AI Attempts"
          description="View all student AI interview attempts and scores"
        />
      )}

      {/* Compact Toolbar */}
      <div className="ai-toolbar">
        {/* Student search using AsyncSelect */}
        <div style={{ minWidth: "280px", flex: "0 0 auto" }}>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadStudentOptions}
            value={selectedStudent}
            onChange={(selected) => {
              setSelectedStudent(selected);
              setFilters(f => ({ ...f, studentId: selected?.value || "" }));
              setPage(1);
            }}
            placeholder="Search student..."
            isClearable
            styles={customSelectStyles}
          />
        </div>

        <div className="ai-search">
          <Search size={16} className="ai-search-icon" />
          <input
            type="text"
            className="ai-search-input"
            placeholder="Search answer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="ai-toolbar-actions">
          {/* Desktop inline filters */}
          <select
            className="ai-filter-select d-none d-md-block"
            value={filters.techStack}
            onChange={(e) => {
              setFilters(f => ({ ...f, techStack: e.target.value }));
              setPage(1);
            }}
          >
            {techStacks.map((t) => (
              <option key={t} value={t}>{t || "All Tech Stacks"}</option>
            ))}
          </select>
          <select
            className="ai-filter-select d-none d-md-block"
            value={filters.difficulty}
            onChange={(e) => {
              setFilters(f => ({ ...f, difficulty: e.target.value }));
              setPage(1);
            }}
          >
            {difficulties.map((d) => (
              <option key={d} value={d}>{d || "All Difficulties"}</option>
            ))}
          </select>

          {/* Mobile filter button */}
          <button
            className={`ai-filter-btn d-md-none${activeFilterCount > 0 ? " ai-filter-btn--active" : ""}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="ai-filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={attempts}
        columns={columns}
        loading={loading}
        hoverable={true}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: totalRecords,
          onPageChange: setPage
        }}
        emptyMessage="No AI attempts found"
        renderExpandedRow={(row) => {
          const rowId = row._id || row.id;
          if (!expandedRows.has(rowId)) return null;
          return (
            <tr className="ai-expanded-row" key={`expanded-${rowId}`}>
              <td colSpan={columns.length} style={{ padding: 0, border: "none" }}>
                <div className="ai-expanded-detail">
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <HelpCircle size={14} />
                      Question
                    </div>
                    <div className="ai-detail-value">{row.question?.question || "-"}</div>
                  </div>
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <MessageSquare size={14} />
                      Student's Answer
                    </div>
                    <div className="ai-detail-value">{row.answer || "-"}</div>
                  </div>
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <CheckCircle size={14} />
                      AI Feedback
                    </div>
                    <div className="ai-detail-value">{row.feedback || "-"}</div>
                  </div>
                  {row.attemptCount > 1 && (
                    <div className="ai-detail-section ai-detail-section--inline">
                      <div className="ai-detail-label">
                        <Hash size={14} />
                        Attempts
                      </div>
                      <div className="ai-detail-value">{row.attemptCount}</div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        }}
      />

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Tech Stack">
          <select
            value={filters.techStack}
            onChange={(e) => {
              setFilters(f => ({ ...f, techStack: e.target.value }));
              setPage(1);
            }}
          >
            {techStacks.map((t) => (
              <option key={t} value={t}>{t || "All Tech Stacks"}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Difficulty">
          <select
            value={filters.difficulty}
            onChange={(e) => {
              setFilters(f => ({ ...f, difficulty: e.target.value }));
              setPage(1);
            }}
          >
            {difficulties.map((d) => (
              <option key={d} value={d}>{d || "All Difficulties"}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Min Score">
          <input
            type="number"
            value={filters.minScore}
            onChange={(e) => {
              setFilters(f => ({ ...f, minScore: e.target.value }));
              setPage(1);
            }}
            placeholder="Min Score"
          />
        </FilterGroup>
        <FilterGroup label="Max Score">
          <input
            type="number"
            value={filters.maxScore}
            onChange={(e) => {
              setFilters(f => ({ ...f, maxScore: e.target.value }));
              setPage(1);
            }}
            placeholder="Max Score"
          />
        </FilterGroup>
      </FilterModal>
    </div>
  );
};

export default AdminAIAttemptHistory;
